/**
 * SocietyDetail.jsx v7
 * CHANGES:
 *   - Top bar cleaned up: society name on left, all actions on right
 *   - "← Society Book" renamed to "← Back"
 *   - Logout button added to top bar
 *   - Theme toggle moved to top bar
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Building2, BookOpen, Calculator, LayoutGrid,
  Plus, AlertTriangle, Lock, Calendar, ChevronRight, Landmark, Wallet,
  LogOut, Sun, Moon,
} from 'lucide-react';
import { useAuth }  from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { getSociety } from '../../api/society.api';
import { createDeletionRequest, getMyDeletionRequests, getFYs, createFY } from '../../api/yea.api';
import FYList     from '../accounting/FYList';
import SocietyBook from '../societybook/index';
import Button from '../../components/common/Button';
import Input  from '../../components/common/Input';
import Modal  from '../../components/common/Modal';
import './SocietyDetail.css';

// ── FY Options helper ─────────────────────────────────────────────────────────
const generateFYOptions = () => {
  const now = new Date();
  const currentYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const options = [];
  for (let y = currentYear - 8; y <= currentYear + 7; y++) {
    options.push({
      label:     `${y}-${String(y + 1).slice(2)}`,
      startDate: `${y}-04-01`,
      endDate:   `${y + 1}-03-31`,
    });
  }
  return options;
};
const FY_OPTIONS = generateFYOptions();
const emptyBank  = () => ({ bankName: '', accountNumber: '', openingBalance: '' });

// ── Inline FY Create Form ─────────────────────────────────────────────────────
const FYCreateForm = ({ societyId, onCreated, onCancel }) => {
  const [fyName, setFyName] = useState(FY_OPTIONS[3]?.label || '');
  const [cashOB, setCashOB] = useState('');
  const [banks,  setBanks]  = useState([emptyBank()]);
  const [saving, setSaving] = useState(false);

  const selectedFY    = FY_OPTIONS.find(f => f.label === fyName);
  const addBankRow    = () => setBanks(b => [...b, emptyBank()]);
  const removeBankRow = i  => setBanks(b => b.filter((_, idx) => idx !== i));
  const setBankField  = (i, field, val) => setBanks(b => {
    const copy = [...b]; copy[i] = { ...copy[i], [field]: val }; return copy;
  });

  const handleSave = async () => {
    if (!fyName) { toast.error('Select a financial year'); return; }
    const validBanks = banks
      .filter(b => b.bankName.trim())
      .map(b => ({ bankName: b.bankName.trim(), accountNumber: b.accountNumber.trim(), openingBalance: parseFloat(b.openingBalance) || 0 }));
    setSaving(true);
    try {
      const res = await createFY(societyId, {
        fyName, startDate: selectedFY.startDate, endDate: selectedFY.endDate,
        cashOpeningBalance: parseFloat(cashOB) || 0,
        bankOpeningBalances: validBanks,
      });
      const fy = res.data.fy || res.data.financialYear;
      toast.success(`Financial Year ${fyName} created`);
      onCreated(fy);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to create FY'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
      <div className="field">
        <label className="field-label">Financial Year <span className="required">*</span></label>
        <div className="field-control">
          <select className="field-input" value={fyName} onChange={e => setFyName(e.target.value)}>
            {FY_OPTIONS.map(o => <option key={o.label} value={o.label}>{o.label}</option>)}
          </select>
        </div>
        {selectedFY && (
          <div style={{ fontSize:'0.78rem', color:'var(--text-dim)', marginTop:'0.3rem' }}>
            {selectedFY.startDate} to {selectedFY.endDate}
          </div>
        )}
      </div>
      <Input label="Cash Opening Balance (₹)" type="number" min="0" placeholder="0"
        value={cashOB} onChange={e => setCashOB(e.target.value)} />
      <div>
        <div style={{ fontSize:'0.8rem', fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'0.6rem' }}>
          Bank Accounts
        </div>
        {banks.map((b, i) => (
          <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 140px 36px', gap:'0.5rem', marginBottom:'0.5rem', alignItems:'center' }}>
            <input type="text" placeholder="Bank Name *" value={b.bankName}
              onChange={e => setBankField(i, 'bankName', e.target.value)} className="field-input" style={{ fontSize:'0.82rem' }} />
            <input type="text" placeholder="Account No" value={b.accountNumber}
              onChange={e => setBankField(i, 'accountNumber', e.target.value)} className="field-input" style={{ fontSize:'0.82rem' }} />
            <input type="number" placeholder="Opening ₹" value={b.openingBalance}
              onChange={e => setBankField(i, 'openingBalance', e.target.value)} className="field-input" style={{ fontSize:'0.82rem' }} onWheel={e => e.target.blur()} />
            <button onClick={() => removeBankRow(i)} disabled={banks.length === 1}
              style={{ width:32, height:32, background:'none', border:'1px solid var(--border-subtle)', borderRadius:'var(--radius-sm)', cursor: banks.length === 1 ? 'not-allowed' : 'pointer', color:'var(--danger)', opacity: banks.length === 1 ? 0.3 : 1, display:'flex', alignItems:'center', justifyContent:'center' }}>
              ×
            </button>
          </div>
        ))}
        <button onClick={addBankRow}
          style={{ background:'none', border:'none', cursor:'pointer', color:'var(--gold)', fontSize:'0.8rem', fontWeight:600, display:'flex', alignItems:'center', gap:'0.3rem', padding:'0.2rem 0' }}>
          <Plus size={13} /> Add another bank
        </button>
      </div>
      <div style={{ display:'flex', gap:'0.5rem', justifyContent:'flex-end' }}>
        {onCancel && <Button variant="subtle" onClick={onCancel}>Cancel</Button>}
        <Button loading={saving} icon={Plus} onClick={handleSave}>Create Financial Year</Button>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const SocietyDetail = () => {
  const { societyId } = useParams();
  const navigate      = useNavigate();
  const { logout }    = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();

  const [society,      setSociety]      = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [activeTab,    setActiveTab]    = useState('overview');
  const [deleteModal,  setDeleteModal]  = useState(false);
  const [formLoading,  setFormLoading]  = useState(false);
  const [pendingReq,   setPendingReq]   = useState(false);
  const [deleteReason, setDeleteReason] = useState('');

  const [fyLoading,    setFyLoading]    = useState(true);
  const [fys,          setFys]          = useState([]);
  const [activeFY,     setActiveFY]     = useState(null);
  const [showFYCreate, setShowFYCreate] = useState(false);

  const hasFY     = fys.length > 0;
  const hasModule = key => society?.activeModules?.includes(key);
  const hasAnyMod = hasModule('year_end_accounting') || hasModule('society_book');

  const fetchSociety = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSociety(societyId);
      setSociety(res.data.society);
    } catch { toast.error('Failed to load society'); navigate('/dashboard'); }
    finally { setLoading(false); }
  }, [societyId, navigate]);

  const fetchFYs = useCallback(async () => {
    setFyLoading(true);
    try {
      const res  = await getFYs(societyId);
      const list = res.data.fys || res.data.financialYears || [];
      setFys(list);
      setActiveFY(list[0] || null);
    } catch { setFys([]); setActiveFY(null); }
    finally { setFyLoading(false); }
  }, [societyId]);

  useEffect(() => { fetchSociety(); }, [fetchSociety]);
  useEffect(() => { fetchFYs(); }, [fetchFYs]);
  useEffect(() => {
    getMyDeletionRequests().then(r => {
      const hasPending = r.data.requests.some(
        req => String(req.targetId) === String(societyId) && req.status === 'pending'
      );
      setPendingReq(hasPending);
    }).catch(() => {});
  }, [societyId]);

  const handleFYCreated = () => { setShowFYCreate(false); fetchFYs(); };

  const handleDeleteRequest = async () => {
    setFormLoading(true);
    try {
      await createDeletionRequest({
        type: 'society', targetId: societyId,
        targetName: society.name, societyId,
        reason: deleteReason || 'Requested by user',
      });
      toast.success('Deletion request submitted. Admin will review it.');
      setDeleteModal(false); setDeleteReason(''); setPendingReq(true);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to submit request'); }
    finally { setFormLoading(false); }
  };

  if (loading || fyLoading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!society) return null;

  const tabs = [{ key:'overview', label:'Overview', Icon:LayoutGrid }];

  const statusColor = s => ({ none:'badge-subtle', pending_edit:'badge-warning', pending_delete:'badge-danger', approved_edit:'badge-success', approved_delete:'badge-danger', rejected:'badge-info' }[s] || 'badge-subtle');
  const statusLabel = s => ({ none:'', pending_edit:'Edit Pending', pending_delete:'Delete Pending', approved_edit:'Edit Approved', approved_delete:'Deleted', rejected:'Rejected' }[s] || '');

  return (
    <div className="society-layout">

      {/* ── Top bar ── */}
      <header className="society-topbar" style={{ background: '#C75B39', borderBottom: '1px solid #A64B2A' }}>

        {/* LEFT: Society name + badges */}
        <div className="society-topbar-info">
          <Building2 size={16} color="#FFFFFF" strokeWidth={1.5} />
          <h2 style={{ color: '#FFFFFF' }}>{society.name}</h2>
          {society.registrationNumber && (
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.75)' }}>Reg: {society.registrationNumber}</span>
          )}
          
          {pendingReq && <span className="badge badge-warning" style={{ background:'rgba(255,255,255,0.2)', color:'#FFFFFF', border:'1px solid rgba(255,255,255,0.3)' }}>Delete Pending</span>}
        </div>

        {/* RIGHT: All actions */}
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginLeft:'auto', flexShrink: 0 }}>
          {/* Theme toggle */}
          <button onClick={toggleTheme} title="Toggle theme"
            style={{ background:'var(--bg-raised)', border:'1px solid var(--border-subtle)', borderRadius:'var(--radius-sm)', width:32, height:32, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'var(--text-muted)' }}>
            {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
          </button>
          {/* Back to societies */}
          <Button size="xs" variant="subtle" icon={ArrowLeft} onClick={() => navigate('/dashboard')}>
            Back
          </Button>
          {/* Request delete */}
          <Button size="xs" variant="danger" icon={AlertTriangle}
  onClick={() => setDeleteModal(true)} disabled={pendingReq}
  style={{ background:'rgba(255,255,255,0.2)', color:'#FFFFFF', border:'1px solid rgba(255,255,255,0.3)' }}>
  {pendingReq ? 'Delete Pending' : 'Request Delete'}
</Button>
          {/* Logout */}
          <Button size="xs" variant="subtle" icon={LogOut}
            onClick={() => { logout(); navigate('/user/login'); }}>
            Logout
          </Button>
        </div>
      </header>

      {/* Tab bar */}
      <div className="society-tab-bar">
        {tabs.map(({ key, label, Icon }) => (
          <button key={key} className={`s-tab ${activeTab === key ? 'active' : ''}`}
            onClick={() => setActiveTab(key)}>
            <Icon /> {label}
          </button>
        ))}
      </div>

      <div className="society-content">

        {/* ── OVERVIEW ── */}
        {activeTab === 'overview' && (
          <div className="animate-in">
            <div className="overview-grid" style={{ marginBottom:'1.75rem' }}>
              <div className="card">
                <h3 className="mb-2">Society Information</h3>
                <div className="info-list">
                  <div className="info-row"><span className="info-row-label">Name</span><span className="info-row-value">{society.name}</span></div>
                  <div className="info-row"><span className="info-row-label">Registration</span><span className="info-row-value">{society.registrationNumber||'—'}</span></div>
                  <div className="info-row"><span className="info-row-label">Active FY</span><span className="info-row-value">{activeFY?.fyName||'—'}</span></div>
                  <div className="info-row"><span className="info-row-label">City</span><span className="info-row-value">{society.address?.city||'—'}</span></div>
                  <div className="info-row"><span className="info-row-label">State</span><span className="info-row-value">{society.address?.state||'—'}</span></div>
                </div>
              </div>
              <div className="card">
                <h3 className="mb-2">Statistics</h3>
                <div className="overview-stat-grid">
                  <div className="overview-stat">
                    <div className="overview-stat-val">{society.members?.filter(m=>m.isActive)?.length ?? 0}</div>
                    <div className="overview-stat-lbl">Active Members</div>
                  </div>
                  <div className="overview-stat">
                    <div className="overview-stat-val">{fys.length}</div>
                    <div className="overview-stat-lbl">Financial Years</div>
                  </div>
                </div>
              </div>
            </div>

            {hasAnyMod && (
              <div className="card" style={{ padding:'1.5rem' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1.25rem' }}>
                  <div>
                    <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.05rem' }}>Financial Years</h3>
                    <p className="text-sm text-muted" style={{ marginTop:'0.2rem' }}>
                      Opening balances are shared across Year End Accounting and Society Book
                    </p>
                  </div>
                  {!showFYCreate && (
                    <Button size="sm" icon={Plus} onClick={() => setShowFYCreate(true)}>New Financial Year</Button>
                  )}
                </div>

                {showFYCreate && (
                  <div style={{ marginBottom:'1.5rem', padding:'1.25rem', background:'var(--bg-raised)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border-subtle)' }}>
                    <div style={{ fontWeight:600, fontSize:'0.85rem', marginBottom:'1rem', color:'var(--text)' }}>Create New Financial Year</div>
                    <FYCreateForm societyId={societyId} onCreated={handleFYCreated} onCancel={() => setShowFYCreate(false)} />
                  </div>
                )}

                {!hasFY && !showFYCreate && (
                  <div style={{ textAlign:'center', padding:'2rem', color:'var(--text-muted)' }}>
                    <Calendar size={36} strokeWidth={1} style={{ marginBottom:'0.75rem', opacity:0.4 }} />
                    <div style={{ fontWeight:600, marginBottom:'0.35rem' }}>No Financial Years yet</div>
                    <div style={{ fontSize:'0.82rem' }}>Create a Financial Year to start using Year End Accounting and Society Book</div>
                  </div>
                )}

                {hasFY && (
                  <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem' }}>
                    {fys.map(fy => (
                      <div key={fy._id}
                        onClick={() => navigate(`/dashboard/society/${societyId}/fy/${fy._id}`)}
                        style={{ display:'flex', alignItems:'center', gap:'1rem', padding:'1rem 1.25rem', background:'var(--bg-raised)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border-subtle)', cursor:'pointer', transition:'all var(--t-fast)' }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--gold)'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'}>
                        <div style={{ flex:1 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:'0.6rem', marginBottom:'0.3rem' }}>
                            <span style={{ fontFamily:'var(--font-display)', fontWeight:700, fontSize:'1rem', color:'var(--gold)' }}>{fy.fyName}</span>
                            {fy.approvalStatus !== 'none' && statusLabel(fy.approvalStatus) && (
                              <span className={`badge ${statusColor(fy.approvalStatus)}`}>{statusLabel(fy.approvalStatus)}</span>
                            )}
                          </div>
                          <div style={{ fontSize:'0.78rem', color:'var(--text-dim)', display:'flex', alignItems:'center', gap:'0.3rem' }}>
                            <Calendar size={11}/>
                            {new Date(fy.startDate).toLocaleDateString('en-IN')} — {new Date(fy.endDate).toLocaleDateString('en-IN')}
                          </div>
                        </div>
                        <div style={{ display:'flex', gap:'1.25rem', flexShrink:0 }}>
                          <div style={{ textAlign:'right' }}>
                            <div style={{ fontSize:'0.7rem', color:'var(--text-dim)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'0.15rem', display:'flex', alignItems:'center', gap:'0.25rem', justifyContent:'flex-end' }}>
                              <Wallet size={10}/> Cash
                            </div>
                            <div style={{ fontWeight:600, fontSize:'0.875rem', color:'var(--text)' }}>
                              ₹{(fy.cashOpeningBalance||0).toLocaleString('en-IN')}
                            </div>
                          </div>
                          <div style={{ textAlign:'right' }}>
                            <div style={{ fontSize:'0.7rem', color:'var(--text-dim)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'0.15rem', display:'flex', alignItems:'center', gap:'0.25rem', justifyContent:'flex-end' }}>
                              <Landmark size={10}/> Banks
                            </div>
                            <div style={{ fontWeight:600, fontSize:'0.875rem', color:'var(--text)' }}>
                              {fy.bankOpeningBalances?.length || 0}
                            </div>
                          </div>
                        </div>
                        <ChevronRight size={15} color="var(--text-dim)" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {!hasAnyMod && (
              <div className="card" style={{ textAlign:'center', padding:'3rem' }}>
                <Lock size={32} strokeWidth={1} style={{ opacity:0.25, margin:'0 auto 1rem', display:'block' }}/>
                <h3>No Modules Assigned</h3>
                <p className="text-muted text-sm mt-1">Contact your administrator to enable modules.</p>
              </div>
            )}
          </div>
        )}

        {/* ── YEAR END ACCOUNTING ── */}
        {activeTab === 'yea' && (
          <div className="animate-in">
            {!hasFY ? (
              <div className="empty-state card">
                <Calendar size={40} strokeWidth={1} />
                <h3>No Financial Year</h3>
                <p>Go to the Overview tab to create a Financial Year first</p>
                <Button size="sm" icon={LayoutGrid} onClick={() => setActiveTab('overview')} className="mt-2">
                  Go to Overview
                </Button>
              </div>
            ) : (
              <FYList societyId={societyId} societyName={society.name} />
            )}
          </div>
        )}

        {/* ── SOCIETY BOOK ── */}
        {activeTab === 'society_book' && (
          <div className="animate-in">
            {!hasFY ? (
              <div className="empty-state card">
                <Calendar size={40} strokeWidth={1} />
                <h3>No Financial Year</h3>
                <p>Go to the Overview tab to create a Financial Year first</p>
                <Button size="sm" icon={LayoutGrid} onClick={() => setActiveTab('overview')} className="mt-2">
                  Go to Overview
                </Button>
              </div>
            ) : (
              <SocietyBook societyId={societyId} activeFY={activeFY} />
            )}
          </div>
        )}

      </div>

      {/* Delete Request Modal */}
      <Modal isOpen={deleteModal} onClose={() => setDeleteModal(false)}
        title="Request Society Deletion" subtitle="This will be reviewed by admin" size="sm"
        footer={
          <>
            <Button variant="subtle" onClick={() => setDeleteModal(false)}>Cancel</Button>
            <Button variant="danger" loading={formLoading} icon={AlertTriangle} onClick={handleDeleteRequest}>Submit Request</Button>
          </>
        }>
        <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
          <div style={{ padding:'0.85rem', background:'var(--warning-bg)', borderRadius:'var(--radius-sm)', fontSize:'0.82rem', color:'var(--warning)', display:'flex', gap:'0.5rem' }}>
            <AlertTriangle size={14} style={{ flexShrink:0, marginTop:'0.1rem' }}/>
            Societies cannot be deleted directly. Your request will be sent to the administrator for approval.
          </div>
          <Input label="Reason (optional)" placeholder="Why do you want to delete this society?"
            value={deleteReason} onChange={e => setDeleteReason(e.target.value)}/>
        </div>
      </Modal>
    </div>
  );
};

export default SocietyDetail;
